import { useCallback, useEffect, useMemo, useRef } from 'react';

/**
 * Small long-press hook (no deps).
 * - Touch-first (iOS Safari).
 * - Cancels on scroll/move beyond a small threshold.
 * - Also exposes a right-click handler (desktop).
 */
export function useLongPress({
  onLongPress,
  onRightClick,
  delayMs = 525,
  moveTolerancePx = 10,
  enabled = true,
} = {}) {
  const timerRef = useRef(null);
  const startRef = useRef({ x: 0, y: 0, active: false });
  const longPressFiredRef = useRef(false);

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    startRef.current.active = false;
  }, []);

  useEffect(() => clear, [clear]);

  const start = useCallback(
    (clientX, clientY) => {
      if (!enabled) return;
      clear();
      longPressFiredRef.current = false;
      startRef.current = { x: clientX, y: clientY, active: true };
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        if (!startRef.current.active) return;
        longPressFiredRef.current = true;
        onLongPress?.();
      }, delayMs);
    },
    [clear, delayMs, enabled, onLongPress]
  );

  const move = useCallback(
    (clientX, clientY) => {
      if (!enabled) return;
      if (!startRef.current.active) return;
      const dx = Math.abs(clientX - startRef.current.x);
      const dy = Math.abs(clientY - startRef.current.y);
      if (dx > moveTolerancePx || dy > moveTolerancePx) {
        clear();
      }
    },
    [clear, enabled, moveTolerancePx]
  );

  const end = useCallback(() => {
    clear();
  }, [clear]);

  const handlers = useMemo(
    () => ({
      onTouchStart: (e) => {
        if (!enabled) return;
        const t = e.targetTouches?.[0];
        if (!t) return;
        start(t.clientX, t.clientY);
      },
      onTouchMove: (e) => {
        if (!enabled) return;
        const t = e.targetTouches?.[0];
        if (!t) return;
        move(t.clientX, t.clientY);
      },
      onTouchEnd: () => end(),
      onTouchCancel: () => end(),
      onContextMenu: (e) => {
        if (!enabled) return;
        // Right click / long-click trackpads.
        e.preventDefault();
        onRightClick?.();
      },
      onMouseDown: (e) => {
        if (!enabled) return;
        // Optional: only start for primary button; touch devices will use touch handlers.
        if (e.button !== 0) return;
        start(e.clientX, e.clientY);
      },
      onMouseMove: (e) => move(e.clientX, e.clientY),
      onMouseUp: () => end(),
      onMouseLeave: () => end(),
      /**
       * If a long-press fired, prevent the subsequent click from triggering
       * (common on mobile where a press may emit a click after release).
       */
      onClickCapture: (e) => {
        if (longPressFiredRef.current) {
          e.preventDefault();
          e.stopPropagation();
          longPressFiredRef.current = false;
        }
      },
    }),
    [enabled, end, move, onRightClick, start]
  );

  return handlers;
}

